import { api } from './api';

export interface JiraCredentialsInput {
  baseUrl: string;
  email: string;
  apiToken: string;
  /**
   * Auth header style:
   *  - `basic` (default): `Authorization: Basic base64(email:token)` — for
   *    classic Atlassian API tokens with no scopes.
   *  - `bearer`: `Authorization: Bearer <token>` — for scoped API tokens.
   */
  authMode?: 'basic' | 'bearer';
}

export interface JiraMe {
  accountId: string;
  emailAddress: string | null;
  displayName: string;
}

export interface JiraProject {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  avatarUrl?: string | null;
  projectTypeKey?: string | null;
  lead?: { accountId: string; displayName: string } | null;
}

export interface ImportJobRow {
  id: string;
  organizationId: string;
  projectId: string | null;
  source: 'JIRA';
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  remoteKey: string | null;
  remoteName: string | null;
  totalIssues: number;
  importedIssues: number;
  totalUsers: number;
  importedUsers: number;
  totalSprints: number;
  importedSprints: number;
  totalComments: number;
  importedComments: number;
  error: string | null;
  warnings: { messages?: string[] } | null;
  createdById: string;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  project: { id: string; key: string; name: string } | null;
}

export async function jiraValidate(orgId: string, credentials: JiraCredentialsInput) {
  const { data } = await api.post<{ ok: boolean; me: JiraMe }>(
    `/orgs/${orgId}/integrations/jira/validate`,
    credentials,
  );
  return data;
}

export async function jiraListProjects(orgId: string, credentials: JiraCredentialsInput) {
  const { data } = await api.post<{ items: JiraProject[] }>(
    `/orgs/${orgId}/integrations/jira/projects`,
    credentials,
  );
  return data.items;
}

export interface StartJiraImportBody {
  credentials: JiraCredentialsInput;
  jiraProjectId: string;
  jiraProjectKey: string;
  jiraProjectName: string;
  localProjectName: string;
  localProjectKey: string;
  includeComments?: boolean;
}

export async function startJiraImport(orgId: string, body: StartJiraImportBody) {
  const { data } = await api.post<{ jobId: string }>(
    `/orgs/${orgId}/integrations/jira/import`,
    body,
  );
  return data;
}

export async function getJiraImportJob(orgId: string, jobId: string) {
  const { data } = await api.get<ImportJobRow>(
    `/orgs/${orgId}/integrations/jira/import/${jobId}`,
  );
  return data;
}
